import { MediaUpload } from '@wordpress/block-editor';
import {
  Button,
  __experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import {
  MediaReplaceButton,
  MediaRemoveButton,
} from '@components/media-upload';

const {
  i18n: { __ },
  element: { useState },
} = wp;

const durationToMilliseconds = (duration) => {
  const [minutes, seconds] = duration.split(':').map(parseFloat);
  const totalMilliseconds = (minutes * 60 + seconds) * 1000;
  return totalMilliseconds;
};

const PreviewVideo = ({ setAttributes, thumbnailVideo }) => {
  return (
    <>
      <span className="title-label">Preview Video</span>
      <MediaUpload
        onSelect={(file) => {
          setAttributes({
            thumbnailVideo: {
              id: file.id,
              title: file.title,
              filename: file.filename,
              url: file.url,
              fileType: file.type,
              fileFormat: file.mime,
              videoDuration: file.fileLength,
              videoDurationMilliSeconds: durationToMilliseconds(
                file.fileLength,
              ),
            },
          });
        }}
        allowedTypes={['video']}
        value={thumbnailVideo.url}
        render={(mediaUploadProps) => {
          return (
            <div className="media-upload video-upload">
              {thumbnailVideo.url && (
                <div className="upload-buttons-wrapper">
                  <MediaReplaceButton onClick={mediaUploadProps.open} />
                  <MediaRemoveButton
                    onClick={() => setAttributes({ thumbnailVideo: {} })}
                  />
                </div>
              )}
              {!thumbnailVideo.url && (
                <Button
                  className="media-button upload-button"
                  onClick={mediaUploadProps.open}
                >
                  {__('Upload Video', 'berg')}
                </Button>
              )}
              <div className="video-file-name">
                <p>{thumbnailVideo?.filename}</p>
              </div>
            </div>
          );
        }}
      />
    </>
  );
};

const ThumbnailPlayIcon = ({ setAttributes, thumbnailPlayBtn }) => {
  return (
    <>
      <hr />
      <span className="title-label">Play Icon</span>
      <MediaUpload
        onSelect={(file) => {
          setAttributes({
            thumbnailPlayBtn: {
              id: file.id,
              title: file.title,
              filename: file.filename,
              playButtonImgUrl: file.url,
              fileType: file.type,
            },
          });
        }}
        type="image"
        allowedTypes={['image']}
        value={thumbnailPlayBtn.playButtonImgUrl}
        render={({ open }) => (
          <div className="media-upload image-upload">
            {thumbnailPlayBtn.playButtonImgUrl && (
              <div className="upload-buttons-wrapper">
                <MediaReplaceButton onClick={open} />
                <MediaRemoveButton
                  onClick={() => {
                    setAttributes({
                      thumbnailPlayBtn: {},
                    });
                  }}
                />
              </div>
            )}
            {!thumbnailPlayBtn.playButtonImgUrl && (
              <Button className="media-button upload-button" onClick={open}>
                {__('Upload Image', 'b3rg')}
              </Button>
            )}
            {thumbnailPlayBtn.playButtonImgUrl && (
              <div
                className="image-thumb-wrapper"
                onClick={open}
                onKeyUp={open}
                role="button"
                tabIndex="0"
              >
                <img
                  className="image-thumb"
                  src={thumbnailPlayBtn.playButtonImgUrl}
                  alt=""
                />
              </div>
            )}
          </div>
        )}
      />
    </>
  );
};

const ThumbnailVideoOptions = ({
  setAttributes,
  thumbnailVideo,
  thumbnailVideoStartTime,
  thumbnailVideoEndTime,
}) => {
  const [durationValidationStartMsg, setDurationValidationStartMsg] =
    useState('');
  const [durationValidationEndMsg, setDurationValidationEndMsg] = useState('');

  if (!thumbnailVideo.url) return;

  return (
    <>
      <br />
      <span className="hint-text">
        Duration : {thumbnailVideo.videoDuration}
      </span>
      <span className="title-label">Preview Start Time (ms)</span>
      <span className="error-text">{durationValidationStartMsg}</span>
      <NumberControl
        min={0}
        max={thumbnailVideo.videoDurationMilliSeconds}
        value={thumbnailVideoStartTime}
        onChange={(thumbnailVideoStartTime) => {
          const startTime = parseInt(thumbnailVideoStartTime);
          if (startTime <= thumbnailVideo.videoDurationMilliSeconds) {
            setAttributes({ thumbnailVideoStartTime: startTime });
            setDurationValidationStartMsg('');
            if (thumbnailVideoEndTime < thumbnailVideoStartTime) {
              setAttributes({
                thumbnailVideoEndTime: startTime,
              });
            }
          } else {
            setDurationValidationStartMsg(
              'Start time cannot be empty or exceed video duration',
            );
          }
        }}
      />
      <span className="title-label">Preview End Time (ms)</span>
      <span className="error-text">{durationValidationEndMsg}</span>
      <NumberControl
        min={0}
        max={thumbnailVideo.videoDurationMilliSeconds}
        value={thumbnailVideoEndTime}
        onChange={(thumbnailVideoEndTime) => {
          const endTime = parseInt(thumbnailVideoEndTime);
          if (
            endTime <= thumbnailVideo.videoDurationMilliSeconds &&
            endTime >= thumbnailVideoStartTime
          ) {
            setAttributes({ thumbnailVideoEndTime: endTime });
            setDurationValidationEndMsg('');
          } else {
            setDurationValidationEndMsg(
              'End time must be within video duration and not less than start time',
            );
          }
        }}
        help={__(
          'Start and End times must be defined from Milliseconds (1s = 1000 ms).',
          'b3rg',
        )}
      />
    </>
  );
};

const ThumbnailVideoSettings = (props) => {
  const { attributes, setAttributes } = props;
  const {
    thumbnailVideo,
    thumbnailPlayBtn,
    thumbnailVideoStartTime,
    thumbnailVideoEndTime,
  } = attributes;

  return (
    <>
      <PreviewVideo {...{ setAttributes, thumbnailVideo }} />
      {thumbnailVideo.url && (
        <ThumbnailPlayIcon {...{ setAttributes, thumbnailPlayBtn }} />
      )}
      <ThumbnailVideoOptions
        {...{
          setAttributes,
          thumbnailVideo,
          thumbnailVideoStartTime,
          thumbnailVideoEndTime,
        }}
      />
    </>
  );
};

export default ThumbnailVideoSettings;
